import { useCallback, useEffect, useRef, useState } from 'react';
import { Bluetooth, Camera, Check, ChevronLeft, Hash } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { Cargando } from '../ui/EstadoDePantalla';
import { usePaso, useSubpantalla } from '../ui/navegacionReact';
import EscanerCamara from '../mobile/EscanerCamara';
import {
  LARGO_CODIGO, codigoCompleto, faltaEnDonde, formatearCodigo, minutosYSegundos, motivoDeRechazo,
  normalizarCodigo, recortarNombre, useCuentaRegresiva,
} from './codigoDeTerminal';
import { leerErrorDelContrato } from './errorDelContrato';
import { codigoDeLoEscaneado } from './licenciaDeTerminal';
import { CamposDonde, CodigoDeAlta, ErrorDelContrato } from './VincularTerminal';
import CodigoDeActivacion from './CodigoDeActivacion';
import ConfigurarPorBluetooth from './ConfigurarPorBluetooth';
import { hayBluetooth } from './cfgBle';

/* =====================================================================
   «Agregar terminal» · el asistente de tres pasos · F1b (10 oct 2026)
   =====================================================================
   Pedido del dueño con la placa real: el QR de la OLED de 0.96" no lo
   lee ningún teléfono, así que la vía principal es EL CÓDIGO ESCRITO.

     1 · Enciende la terminal y conéctala a tu Wi-Fi (con su dibujo).
     2 · Escribe el código que ves en su pantalla (o escanéalo).
     3 · Ponle nombre y repisa → aprobar → «¡Lista!».

   Si se llega con el código en la dirección (`/t/K7P4QX`, escrito a mano o
   desde el QR), se busca solo y se abre el paso 3.

   La opción secundaria, «Darme un número para teclear en la terminal»,
   es el código de alta de siempre (§2.4) y usa la MISMA regla de licencia.

   EL FRONTEND NO DECIDE (DISEÑO-F1b D12): si la empresa no puede conectar
   otra, lo dice `GET /api/terminales/licencia` con su motivo y qué hacer,
   y el botón principal queda apagado CON ese motivo a la vista.

   TA-7 §6 · «ACTIVAR ESTA TERMINAL» (contrato 2.10.0, DISEÑO-TA7 §4.3 y
   §8; deuda TA7-2, prueba P-28). Una terminal que pidió permiso desde su
   propia pantalla (`emparejar`) enseña el mismo código corto, pero
   `GET vincular/{código}` contesta `modo: "ACTIVAR"`. Entonces el paso 3
   NO pide nombre ni repisa (eso se elige en la terminal): un solo botón
   «Activar esta terminal» que llama a `POST vincular/{código}/activar`,
   apagado CON su motivo si la licencia no alcanza, y el código de la
   tarjeta de la caja encima si `licencia.pide_activacion`.
   ===================================================================== */

const PASOS_AGREGAR = [
  { n: 1, nombre: 'Wi-Fi' },
  { n: 2, nombre: 'Código' },
  { n: 3, nombre: 'Nombre y repisa' },
];

const BOTON = 'min-h-11 w-full rounded-xl px-3 text-sm font-semibold disabled:opacity-50';
const PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;
const ENLACE = 'min-h-11 inline-flex items-center gap-1 text-sm font-semibold text-gray-700';

const ERRORES_VER = { 404: 'CODIGO_DESCONOCIDO', 410: 'CODIGO_VENCIDO', 429: 'DEMASIADAS_PETICIONES' };
const ERRORES_APROBAR = { 404: 'CODIGO_DESCONOCIDO', 409: 'YA_VINCULADA', 403: 'SIN_PERMISO', 429: 'DEMASIADAS_PETICIONES' };
// `vincular/{código}/activar` (TA-7 §4.3): un 409 puede ser de licencia o
// YA_VINCULADA; el código lo trae el servidor y manda (errorDelContrato).
const ERRORES_ACTIVAR = { 404: 'CODIGO_DESCONOCIDO', 403: 'SIN_PERMISO', 429: 'DEMASIADAS_PETICIONES' };
export const LISTO_ACTIVAR = 'Listo. Mira la pantalla de la terminal: te pedirá dónde está.';

// ---------------------------------------------------------------------
// Las dos ilustraciones: SVG propio, sin dependencias, sin texto que
// confiese nada. Se describen con `aria-label` para quien no las ve.
// ---------------------------------------------------------------------
export function IlustracionWifi() {
  return (
    <svg viewBox="0 0 320 170" role="img" aria-label="La terminal enseña en su pantalla una red TEGO y su clave; el teléfono se une a esa red."
         className="h-auto w-full max-w-sm" data-ilustracion="wifi">
      <title>La terminal enseña su red TEGO y su clave; el teléfono se une a ella</title>
      <rect x="10" y="20" width="150" height="130" rx="14" fill="#1f2937" />
      <rect x="24" y="34" width="122" height="62" rx="4" fill="#0b1220" stroke="#38bdf8" strokeWidth="2" />
      <text x="85" y="56" textAnchor="middle" fontFamily="monospace" fontSize="13" fill="#e0f2fe">Red: TEGO-7K2P</text>
      <text x="85" y="80" textAnchor="middle" fontFamily="monospace" fontSize="13" fill="#e0f2fe">Clave: 1234-5678</text>
      <circle cx="85" cy="122" r="13" fill="#374151" stroke="#9ca3af" strokeWidth="2" />
      <path d="M182 72 q14 -14 28 0 M188 80 q8 -8 16 0" fill="none" stroke="#16a34a" strokeWidth="4" strokeLinecap="round" />
      <circle cx="196" cy="88" r="3.5" fill="#16a34a" />
      <rect x="228" y="16" width="80" height="140" rx="12" fill="#ffffff" stroke="#1f2937" strokeWidth="3" />
      <rect x="238" y="32" width="60" height="96" rx="4" fill="#f0fdf4" stroke="#86efac" strokeWidth="1.5" />
      <text x="268" y="58" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#166534">Wi-Fi</text>
      <text x="268" y="78" textAnchor="middle" fontFamily="monospace" fontSize="9" fill="#14532d">TEGO-7K2P</text>
      <path d="M256 98 l8 8 l16 -16" fill="none" stroke="#16a34a" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="268" cy="142" r="5" fill="#d1d5db" />
    </svg>
  );
}

export function IlustracionCodigo({ codigo = 'K7P-4QX' }) {
  return (
    <svg viewBox="0 0 220 120" role="img" aria-label={`La pantalla de la terminal dice Vincular y un código como ${codigo}.`}
         className="h-auto w-full max-w-xs" data-ilustracion="codigo">
      <title>La pantalla de la terminal con el código para vincular</title>
      <rect x="8" y="8" width="204" height="104" rx="14" fill="#1f2937" />
      <rect x="22" y="20" width="176" height="80" rx="4" fill="#0b1220" stroke="#38bdf8" strokeWidth="2" />
      <text x="110" y="46" textAnchor="middle" fontFamily="monospace" fontSize="14" fill="#bae6fd">Vincular</text>
      <text x="110" y="82" textAnchor="middle" fontFamily="monospace" fontSize="28" fontWeight="bold" fill="#ffffff">{codigo}</text>
    </svg>
  );
}

function BarraPasos({ paso, activar = false }) {
  // Con `modo: "ACTIVAR"` el paso 3 es activar: el nombre y el lugar los
  // pide la terminal en su pantalla.
  const pasos = activar ? PASOS_AGREGAR.map((p) => (p.n === 3 ? { ...p, nombre: 'Activar' } : p)) : PASOS_AGREGAR;
  const actual = pasos.find((p) => p.n === paso) || pasos[0];
  return (
    <nav aria-label="Progreso para agregar la terminal" data-barra-pasos="" data-paso-actual={paso}
         className="rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-sm font-semibold text-gray-900">{`Paso ${paso} de ${pasos.length} · ${actual.nombre}`}</p>
      <div className="mt-2 grid grid-cols-3 gap-1" aria-hidden="true">
        {pasos.map((p) => (
          <div key={p.n} className={`h-2 rounded-full ${p.n <= paso ? 'bg-[var(--color-marca)]' : 'bg-gray-200'}`} />
        ))}
      </div>
      <ol className="mt-2 grid grid-cols-3 gap-x-2 text-xs">
        {pasos.map((p) => (
          <li key={p.n} className={`flex items-start gap-1 break-words ${p.n === paso ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
            {p.n < paso && <Check className="mt-0.5 h-3 w-3 shrink-0 text-green-700" aria-hidden="true" />}
            <span className="min-w-0">{p.n}. {p.nombre}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// 2.11.0 (DISEÑO-ACTIVACION-EN-LINEA D12): `sinTarjeta` (opcional, por
// omisión no) es como lo monta «Otras formas de agregar» de
// `VincularEnLinea`: el campo de la tarjeta SALE del flujo del cliente. El
// componente y sus pruebas se quedan (Doctrina 1); sin la prop, todo igual.
export const SIN_TARJETA_MOTIVO = 'Esta terminal se vincula en línea: vuelve a «Agregar terminal» y escribe o escanea el código de su pantalla.';

export default function AgregarTerminal({ contexto, irA, onCerrar, sinTarjeta = false }) {
  const precargado = normalizarCodigo(contexto?.vincular || contexto?.escrito || '').codigo;
  // `tegocloud.mx/t` lo teclea quien YA TIENE el código delante (la página
  // de la terminal se lo dice: «entra a tegocloud.mx/t y escribe el código»,
  // petición F-12): se abre directo en el campo del código.
  const [paso, setPaso] = useState(precargado || contexto?.desdeEnlace ? 2 : 1);
  const [secundaria, setSecundaria] = useState(false);
  const [licencia, setLicencia] = useState(undefined); // undefined = leyendo; null = no se pudo
  const [codigo, setCodigo] = useState(precargado);
  const [rechazo, setRechazo] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [errorCodigo, setErrorCodigo] = useState(null);
  const [escaneando, setEscaneando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [donde, setDonde] = useState({ id_sucursal: '', id_ubicacion: '', nombre: '' });
  const [aprobando, setAprobando] = useState(false);
  const [errorAprobar, setErrorAprobar] = useState(null);
  const [activacion, setActivacion] = useState('');   // F1c: código completo de la tarjeta, o ''
  const [listo, setListo] = useState(null);
  // «Buscar terminal cercana» (contrato 2.5.0 §16) SOLO con Web Bluetooth
  // de verdad: `navigator.bluetooth` y `getAvailability()` verdadero. Sin
  // él (iPhone, Firefox, sin adaptador) el asistente arranca como siempre,
  // sin decir nada técnico (§16.7).
  const [conBluetooth, setConBluetooth] = useState(false);
  const [porBluetooth, setPorBluetooth] = useState(false);
  useEffect(() => {
    let vivo = true;
    hayBluetooth().then((si) => { if (vivo) setConBluetooth(si); });
    return () => { vivo = false; };
  }, []);
  const vence = useCuentaRegresiva(datos?.vence_en);
  // «Escanear el QR» solo donde la cámara puede abrirse (conexión segura);
  // en la red local por http no se ofrece un botón que no haría nada. El
  // escáner de la casa (`EscanerCamara`) es quien pide la cámara.
  const conCamara = typeof window !== 'undefined' && window.isSecureContext === true;

  usePaso('agregar-terminal', listo ? 0 : paso - 1, (n) => {
    if (listo) return;
    if (n < 2) { setDatos(null); setErrorAprobar(null); }
    setPaso(n + 1);
  });
  useSubpantalla('agregar-numero', secundaria, () => setSecundaria(false));
  useSubpantalla('agregar-bluetooth', porBluetooth, () => setPorBluetooth(false));

  const leerLicencia = useCallback(() => {
    setLicencia(undefined);
    api.get('/terminales/licencia').then(setLicencia).catch(() => setLicencia(null));
  }, []);
  useEffect(leerLicencia, [leerLicencia]);

  const buscar = useCallback(async (cod) => {
    setBuscando(true);
    setErrorCodigo(null);
    try {
      const d = await api.get(`/terminal/v2/vincular/${cod}`);
      const sucursales = d.sucursales || [];
      const ubicaciones = d.ubicaciones || [];
      setDatos({ ...d, codigo: cod, sucursales, ubicaciones });
      setDonde({
        id_sucursal: sucursales.length === 1 ? String(sucursales[0].id) : '',
        id_ubicacion: ubicaciones.length === 1 ? String(ubicaciones[0].id) : '',
        nombre: recortarNombre(d.nombre_sugerido || ''),
      });
      setPaso(3);
    } catch (e) {
      setErrorCodigo(leerErrorDelContrato(e, { porEstado: ERRORES_VER }));
    } finally {
      setBuscando(false);
    }
  }, []);

  const buscado = useRef(null);
  useEffect(() => {
    if (!codigoCompleto(precargado) || buscado.current === precargado) return;
    buscado.current = precargado;
    buscar(precargado);
  }, [precargado, buscar]);

  function teclear(e) {
    const { codigo: limpio, rechazados } = normalizarCodigo(e.target.value);
    setCodigo(limpio);
    setRechazo(motivoDeRechazo(rechazados));
    setErrorCodigo(null);
  }

  function escaneado(texto) {
    setEscaneando(false);
    const cod = codigoDeLoEscaneado(texto);
    if (!cod) {
      setErrorCodigo({ codigo: null, mensaje: 'Lo que leyó la cámara no es el código de una terminal.',
                       queHacer: 'Escribe el código que ves en su pantalla: son 6 letras y números.' });
      return;
    }
    setCodigo(cod);
    buscar(cod);
  }

  async function aprobar(e) {
    e.preventDefault();
    setAprobando(true);
    setErrorAprobar(null);
    // F1c · UNA PULSACIÓN: si la terminal pide el código de su caja, primero
    // se activa y después se aprueba. Si la activación queda y la aprobación
    // no, se dice así.
    let activada = false;
    if (lic?.pide_activacion) {
      try {
        await api.post('/terminales/activar', { serie: datos.serie, codigo: activacion });
        activada = true;
      } catch (err) {
        setErrorAprobar(leerErrorDelContrato(err, { terminal: 'esta terminal' }));
        setAprobando(false);
        return;
      }
    }
    try {
      const d = await api.post(`/terminal/v2/vincular/${datos.codigo}/aprobar`, {
        id_sucursal: donde.id_sucursal ? Number(donde.id_sucursal) : null,
        id_ubicacion: Number(donde.id_ubicacion),
        nombre: recortarNombre(donde.nombre).trim(),
      });
      setListo(d);
    } catch (err) {
      const e = leerErrorDelContrato(err, { porEstado: ERRORES_APROBAR, terminal: 'esta terminal' });
      setErrorAprobar(activada
        ? { ...e, mensaje: `La terminal ya es de tu empresa. No se pudo terminar de conectar: ${e.mensaje}` }
        : e);
      if (activada) buscar(datos.codigo);
    } finally {
      setAprobando(false);
    }
  }

  // TA-7 §4.3 · «Activar esta terminal». Si pide el código de su caja, se
  // activa primero (F1c) y después se aprueba, en una sola pulsación.
  async function activarTerminal(e) {
    e.preventDefault();
    setAprobando(true);
    setErrorAprobar(null);
    if (lic?.pide_activacion) {
      try {
        await api.post('/terminales/activar', { serie: datos.serie, codigo: activacion });
      } catch (err) {
        setErrorAprobar(leerErrorDelContrato(err, { terminal: 'esta terminal' }));
        setAprobando(false);
        return;
      }
    }
    try {
      const d = await api.post(`/terminal/v2/vincular/${datos.codigo}/activar`, {});
      setListo({ ...(d || {}), activada: true });
    } catch (err) {
      setErrorAprobar(leerErrorDelContrato(err, { porEstado: ERRORES_ACTIVAR, terminal: 'esta terminal' }));
    } finally {
      setAprobando(false);
    }
  }

  // TERMINAL-CODIGO-TARJETA: en el campo de la tarjeta se escribió o se
  // escaneó el código de OTRA terminal. Se lleva a emparejar ésa: paso 2
  // con su código, y se busca sola.
  function otraTerminal(cod) {
    setDatos(null); setActivacion(''); setErrorAprobar(null);
    setCodigo(cod); setRechazo(null); setErrorCodigo(null);
    setPaso(2);
    buscar(cod);
  }

  function otraVez() {
    setListo(null); setDatos(null); setCodigo(''); setRechazo(null);
    setErrorCodigo(null); setErrorAprobar(null); setPaso(1);
    leerLicencia();
  }

  function verTerminales() {
    if (onCerrar) onCerrar();
    else irA?.('terminales');
  }

  // EL BOTÓN PRINCIPAL DICE POR QUÉ NO SE PUEDE (D14). Con licencia leída y
  // sin derecho (ni plan ni compradas), el motivo y qué hacer van a la vista.
  const sinDerecho = licencia && licencia.puede_conectar === false && !licencia.compradas;
  const motivoSinDerecho = sinDerecho ? `${licencia.motivo || ''} ${licencia.que_hacer || ''}`.trim() : null;
  const lic = datos?.licencia;
  // F1c: la que pide el código de su caja se aprueba en cuanto el código
  // está completo; mientras tanto, el botón dice qué falta.
  const pideActivacion = !!lic?.pide_activacion;
  const faltaActivacion = pideActivacion && !activacion;
  const textoFaltaActivacion = sinTarjeta ? SIN_TARJETA_MOTIVO
    : 'Escribe o escanea el código de activación de la tarjeta de su caja.';
  const noPuedeAprobar = lic && lic.puede_aprobar === false && (!pideActivacion || faltaActivacion);
  const falta3 = datos ? faltaEnDonde(donde, datos.ubicaciones) : null;
  const vencido = vence === 0;
  const faltan = LARGO_CODIGO - codigo.length;
  const modoActivar = datos?.modo === 'ACTIVAR';
  // Lo que apaga «Activar esta terminal», en el orden en que se resuelve.
  const motivoActivar = !modoActivar ? null
    : vencido ? 'El código venció: la terminal enseña otro. Escríbelo en el paso 2.'
    : faltaActivacion ? textoFaltaActivacion
    : noPuedeAprobar ? `${lic.motivo || 'Tu empresa no puede conectar otra terminal.'} ${lic.que_hacer || ''}`.trim()
    : null;

  return (
    <div className="max-w-xl space-y-4" data-terminal="agregar">
      <header className="space-y-1">
        {onCerrar && (
          <button type="button" onClick={onCerrar} className={ENLACE}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver a mis terminales
          </button>
        )}
        <h1 className="text-xl font-bold text-gray-900">Agregar terminal</h1>
        <p className="text-sm text-gray-600">Conecta una terminal nueva a tu empresa en tres pasos, desde el teléfono o la computadora.</p>
      </header>
      <Ayuda id="terminal.agregar.pantalla" />

      {licencia === undefined && <Cargando filas={1} etiqueta="Revisando tus terminales…" />}
      {licencia && licencia.compradas > 0 && (
        <p role="status" className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900" data-aviso-compradas="">
          {licencia.por_conectar > 0
            ? `Tienes ${licencia.por_conectar} ${licencia.por_conectar === 1 ? 'terminal lista' : 'terminales listas'} para conectar.`
            : `Tus ${licencia.compradas} ${licencia.compradas === 1 ? 'terminal comprada ya está conectada' : 'terminales compradas ya están conectadas'}.`}
        </p>
      )}

      {porBluetooth ? (
        <ConfigurarPorBluetooth onCerrar={() => setPorBluetooth(false)} onOtraForma={() => setPorBluetooth(false)}
                                onVerTerminales={verTerminales} />
      ) : secundaria ? (
        <div className="space-y-3">
          <button type="button" onClick={() => setSecundaria(false)} className={ENLACE}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver al asistente
          </button>
          <h2 className="text-base font-semibold text-gray-900">Darme un número para teclear en la terminal</h2>
          <CodigoDeAlta />
        </div>
      ) : (
        <>
          {!listo && paso === 1 && conBluetooth && (
            <section className="space-y-2 rounded-xl border border-gray-200 bg-white p-4" data-buscar-cercana="">
              <h2 className="text-base font-semibold text-gray-900">Buscar terminal cercana</h2>
              <p className="text-sm text-gray-700">La forma más rápida desde esta computadora: por Bluetooth, sin cambiar de red.</p>
              <Ayuda id="terminal.agregar.buscar" />
              <Motivo id="motivo-sin-derecho-ble" cuando={!!sinDerecho}>{motivoSinDerecho}</Motivo>
              <button type="button" onClick={() => setPorBluetooth(true)} disabled={!!sinDerecho}
                      aria-describedby={sinDerecho ? 'motivo-sin-derecho-ble' : undefined} className={PRIMARIO}
                      data-accion="buscar-cercana">
                <span className="inline-flex items-center justify-center gap-2"><Bluetooth className="h-4 w-4" aria-hidden="true" /> Buscar terminal cercana</span>
              </button>
              <p className="pt-1 text-xs text-gray-600">O sigue estos tres pasos con el código de su pantalla:</p>
            </section>
          )}
          {!listo && <BarraPasos paso={paso} activar={modoActivar} />}

          {!listo && paso === 1 && (
            <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-paso="1">
              <h2 className="text-base font-semibold text-gray-900">Enciende la terminal y conéctala a tu Wi-Fi</h2>
              <IlustracionWifi />
              <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">
                <li>Conéctala a la corriente. En su pantalla aparece una red <strong>TEGO-</strong> y su clave.</li>
                <li>En tu teléfono, entra a Wi-Fi y únete a esa red con esa clave. Se abre una página.</li>
                <li>En esa página elige el Wi-Fi de tu negocio y escribe su contraseña.</li>
                <li>La terminal se conecta y enseña un código de 6 letras y números.</li>
              </ol>
              <Ayuda id="terminal.agregar.wifi" />
              <Motivo id="motivo-sin-derecho" cuando={!!sinDerecho}>{motivoSinDerecho}</Motivo>
              <button type="button" onClick={() => setPaso(2)} disabled={!!sinDerecho}
                      aria-describedby={sinDerecho ? 'motivo-sin-derecho' : undefined} className={PRIMARIO}>
                Ya veo el código en su pantalla
              </button>
            </section>
          )}

          {!listo && paso === 2 && (
            <form onSubmit={(e) => { e.preventDefault(); if (codigoCompleto(codigo)) buscar(codigo); }}
                  className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-paso="2">
              <h2 className="text-base font-semibold text-gray-900">Escribe el código que ves en su pantalla</h2>
              <IlustracionCodigo />
              <div>
                <label htmlFor="agregar-codigo" className="block text-sm font-medium text-gray-700">Código de la terminal</label>
                <input id="agregar-codigo" className="entrada mt-1 text-center font-mono text-2xl tracking-widest"
                       value={codigo} onChange={teclear} inputMode="text" autoCapitalize="characters"
                       autoComplete="one-time-code" spellCheck={false} maxLength={LARGO_CODIGO + 2}
                       placeholder="K7P-4QX" disabled={buscando} />
                <Ayuda id="terminal.vincular.codigo" />
                {codigoCompleto(codigo) && (
                  <p className="mt-1 text-xs text-gray-600">En la terminal se ve así: <strong>{formatearCodigo(codigo)}</strong></p>
                )}
                {rechazo && <p role="alert" className="mt-1 text-sm text-red-700">{rechazo}</p>}
              </div>
              <ErrorDelContrato error={errorCodigo} />
              {buscando && <Cargando filas={2} etiqueta="Buscando la terminal" />}
              <Motivo id="motivo-agregar-codigo" cuando={!codigoCompleto(codigo) && !buscando}>
                {`Faltan ${faltan} ${faltan === 1 ? 'carácter' : 'caracteres'}: el código tiene ${LARGO_CODIGO}.`}
              </Motivo>
              <Motivo id="motivo-sin-derecho-codigo" cuando={!!sinDerecho}>{motivoSinDerecho}</Motivo>
              <button type="submit" disabled={!codigoCompleto(codigo) || buscando || !!sinDerecho}
                      aria-describedby={sinDerecho ? 'motivo-sin-derecho-codigo'
                        : !codigoCompleto(codigo) ? 'motivo-agregar-codigo' : undefined} className={PRIMARIO}>
                {buscando ? 'Buscando…' : 'Buscar la terminal'}
              </button>
              {conCamara && !escaneando && (
                <button type="button" onClick={() => setEscaneando(true)} className={SECUNDARIO}>
                  <Camera className="mr-1 inline h-4 w-4" aria-hidden="true" /> Escanear el QR
                </button>
              )}
              {escaneando && <EscanerCamara onCodigo={escaneado} onCerrar={() => setEscaneando(false)} />}
              <button type="button" onClick={() => setPaso(1)} className={SECUNDARIO}>Volver al paso 1</button>
            </form>
          )}

          {!listo && paso === 3 && datos && modoActivar && (
            <form onSubmit={activarTerminal} className="space-y-4 rounded-xl border border-gray-200 bg-white p-4"
                  data-paso="3" data-terminal="activar">
              <div className="space-y-1">
                <h2 className="text-base font-semibold text-gray-900">Activar esta terminal</h2>
                <p className="text-sm text-gray-600">La terminal te pedirá en su pantalla dónde está.</p>
                <Ayuda id="terminal.activar.pantalla" />
              </div>
              {lic?.pide_activacion && !sinTarjeta && (
                <CodigoDeActivacion serie={datos.serie} onCodigo={setActivacion}
                                    codigoDePantalla={datos.codigo} onOtraTerminal={otraTerminal} />
              )}
              <div className="space-y-1 text-sm text-gray-700">
                <div className="break-words">Terminal {formatearCodigo(datos.codigo)} · serie {datos.serie}</div>
                {lic?.via === 'COMPRA' && (
                  <p className="inline-flex rounded-full border border-green-300 bg-green-50 px-2 py-1 text-xs font-semibold text-green-800">
                    Es una de tus terminales compradas
                  </p>
                )}
                {vence !== null && (
                  <div role="status" className={vencido ? 'font-semibold text-red-700' : 'text-gray-600'}>
                    {vencido ? 'Este código ya venció: la terminal enseña otro. Vuelve al paso 2 con el nuevo.'
                             : `Te quedan ${minutosYSegundos(vence)} para activarla.`}
                  </div>
                )}
              </div>
              <ErrorDelContrato error={errorAprobar} />
              {aprobando && <Cargando filas={1} etiqueta="Activando la terminal" />}
              <Motivo id="motivo-activar-terminal" cuando={!!motivoActivar && !aprobando}>{motivoActivar}</Motivo>
              <button type="submit" disabled={!!motivoActivar || aprobando}
                      aria-describedby={motivoActivar ? 'motivo-activar-terminal' : undefined}
                      aria-busy={aprobando || undefined}
                      className={PRIMARIO} data-accion="activar-terminal">
                {aprobando ? 'Activando…' : 'Activar esta terminal'}
              </button>
              <Ayuda id="terminal.activar.boton" />
              <Ayuda id="terminal.activar.ficha" />
              <button type="button" onClick={() => { setDatos(null); setPaso(2); }} className={SECUNDARIO}>Cambiar el código</button>
            </form>
          )}

          {!listo && paso === 3 && datos && !modoActivar && (
            <form onSubmit={aprobar} className="space-y-4 rounded-xl border border-gray-200 bg-white p-4" data-paso="3">
              {/* F1c: una terminal de fábrica sin dueño pide el código de su caja
                  antes de aprobar; al activarse se vuelve a leer su licencia. */}
              {lic?.pide_activacion && !sinTarjeta && (
                <CodigoDeActivacion serie={datos.serie} onCodigo={setActivacion}
                                    codigoDePantalla={datos.codigo} onOtraTerminal={otraTerminal} />
              )}
              <h2 className="text-base font-semibold text-gray-900">Ponle nombre y repisa</h2>
              <div className="space-y-1 text-sm text-gray-700">
                <div className="break-words">Terminal {formatearCodigo(datos.codigo)} · serie {datos.serie}</div>
                {lic?.via === 'COMPRA' && (
                  <p className="inline-flex rounded-full border border-green-300 bg-green-50 px-2 py-1 text-xs font-semibold text-green-800">
                    Es una de tus terminales compradas
                  </p>
                )}
                {vence !== null && (
                  <div role="status" className={vencido ? 'font-semibold text-red-700' : 'text-gray-600'}>
                    {vencido ? 'Este código ya venció: la terminal enseña otro. Vuelve al paso 2 con el nuevo.'
                             : `Te quedan ${minutosYSegundos(vence)} para aprobarla.`}
                  </div>
                )}
              </div>
              <CamposDonde idBase="agregar" sucursales={datos.sucursales} ubicaciones={datos.ubicaciones}
                           valor={donde} onCambio={setDonde} />
              <ErrorDelContrato error={errorAprobar} />
              <Motivo id="motivo-agregar-aprobar" cuando={!!(falta3 || vencido || noPuedeAprobar) && !aprobando}>
                {faltaActivacion ? textoFaltaActivacion
                  : noPuedeAprobar ? `${lic.motivo} ${lic.que_hacer || ''}`.trim()
                  : vencido ? 'El código venció: pide el nuevo a la terminal.' : falta3}
              </Motivo>
              <button type="submit" disabled={!!falta3 || vencido || noPuedeAprobar || aprobando}
                      aria-describedby={falta3 || vencido || noPuedeAprobar ? 'motivo-agregar-aprobar' : undefined}
                      className={PRIMARIO}>
                {aprobando ? 'Conectando…' : (pideActivacion ? 'Activar y aprobar' : 'Aprobar y conectar')}
              </button>
              <button type="button" onClick={() => { setDatos(null); setPaso(2); }} className={SECUNDARIO}>Cambiar el código</button>
            </form>
          )}

          {listo && (
            <div className="space-y-3 rounded-xl border-2 border-green-300 bg-green-50 p-4" data-paso="lista">
              <div role="status" className="flex items-start gap-2 text-sm text-green-900">
                <Check className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div className="min-w-0 break-words">
                  <strong className="block text-base">{listo.activada ? 'Activada' : '¡Lista!'}</strong>
                  {listo.activada ? (
                    <span>{listo.mensaje || LISTO_ACTIVAR}</span>
                  ) : (
                    <>
                      <span>{listo.mensaje || 'La terminal ya es de tu empresa. En unos segundos dirá «¡Lista!».'}</span>
                      <span className="block">Se llama «{listo.nombre || recortarNombre(donde.nombre)}».</span>
                    </>
                  )}
                </div>
              </div>
              <button type="button" onClick={verTerminales} className={PRIMARIO}>Ver mis terminales</button>
              <button type="button" onClick={otraVez} className={SECUNDARIO}>Agregar otra</button>
            </div>
          )}

          {!listo && (
            <div className="space-y-1">
              <button type="button" onClick={() => setSecundaria(true)} disabled={!!sinDerecho}
                      aria-describedby={sinDerecho ? 'motivo-sin-derecho-num' : undefined} className={SECUNDARIO}>
                <Hash className="mr-1 inline h-4 w-4" aria-hidden="true" /> Darme un número para teclear en la terminal
              </button>
              <Motivo id="motivo-sin-derecho-num" cuando={!!sinDerecho}>{motivoSinDerecho}</Motivo>
              <Ayuda id="terminal.agregar.numero" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
